import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/SubscriptionsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the subscriptions list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous keys
// were `['subscriptions', currentToken]` and
// `['subscriptions', {customerId}, currentToken]` — a bare string plus a page
// token, which both collides across modules and makes the cache page-shaped
// rather than list-shaped.
export const subscriptionsKeys = {
    all: ['subscriptions'] as const,
    lists: () => [...subscriptionsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) =>
        [...subscriptionsKeys.lists(), {filters}] as const,
    details: () => [...subscriptionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...subscriptionsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which read
 * as compliant (a query hook, a page control, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe.
 * `useInfiniteQuery` concatenates pages instead, so the client holds a growing
 * loaded window and the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 *
 * ⚠️ `customerId` IS SERVER-SIDE. Unlike the customers list, `listSubscriptions`
 * accepts a `filter` argument, and the EditPage embed relies on it: the
 * subscriptions table on a customer page must show that customer's rows, not
 * the loaded window filtered client-side. It is therefore in the queryKey (§9)
 * — omit it and switching customer silently serves the previous one's cache.
 */
export const useSubscriptionsInfiniteQuery = (customerId?: string) => {
    const filter = customerId ? {customerId} : undefined;

    return useInfiniteQuery({
        queryKey: subscriptionsKeys.list(customerId ? {customerId} : {}),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(filter, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

/**
 * Batch delete. The list key is invalidated on success so the table reflects
 * the removal without a manual reload (§8).
 */
export const useDeleteSubscriptionsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Subscription deleted'
                    : `${ids.length} subscriptions deleted`,
            );
            queryClient.invalidateQueries({queryKey: subscriptionsKeys.all});
        },
        onError: notifyError,
    });
};
